// Utility functions for agency slug generation and validation
import { supabase } from '../lib/supabase'

/**
 * Generate a URL-safe slug from agency name
 */
export function generateSlugFromName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with hyphens
    .replace(/[^a-z0-9]+/g, '-')
    // Remove multiple consecutive hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-|-$/g, '')
    // Ensure minimum length
    .padEnd(3, '0')
    // Ensure maximum length
    .substring(0, 50)
}

/**
 * Validate slug format (client-side validation)
 */
export function isValidSlugFormat(slug: string): boolean {
  // Must be 3-50 characters, lowercase alphanumeric with hyphens
  // Cannot start or end with hyphen
  const slugRegex = /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/
  return slug.length >= 3 && slug.length <= 50 && slugRegex.test(slug)
}

/**
 * Check if slug is available (not taken by another agency)
 */
export async function isSlugAvailable(slug: string, excludeAgencyId?: string): Promise<boolean> {
  try {
    let query = supabase
      .from('agencies')
      .select('id')
      .eq('slug', slug)

    // Exclude current agency when updating
    if (excludeAgencyId) {
      query = query.neq('id', excludeAgencyId)
    }

    const { data, error } = await query.single()

    if (error && error.code === 'PGRST116') {
      // No rows found - slug is available
      return true
    }

    if (error) {
      console.error('Error checking slug availability:', error)
      return false
    }

    // If we found a record, slug is taken
    return false
  } catch (error) {
    console.error('Error checking slug availability:', error)
    return false
  }
}

/**
 * Generate suggestions for alternative slugs when the desired one is taken
 */
export async function generateSlugSuggestions(baseSlug: string, excludeAgencyId?: string): Promise<string[]> {
  const suggestions: string[] = []
  
  // Try base slug first
  if (await isSlugAvailable(baseSlug, excludeAgencyId)) {
    suggestions.push(baseSlug)
  }

  // Generate numbered variations
  for (let i = 1; i <= 5; i++) {
    const suggestion = `${baseSlug}${i}`
    if (suggestion.length <= 50 && await isSlugAvailable(suggestion, excludeAgencyId)) {
      suggestions.push(suggestion)
    }
  }

  // Generate variations with common suffixes
  const suffixes = ['cars', 'rental', 'auto', 'motors']
  for (const suffix of suffixes) {
    const suggestion = `${baseSlug}-${suffix}`
    if (suggestion.length <= 50 && await isSlugAvailable(suggestion, excludeAgencyId)) {
      suggestions.push(suggestion)
    }
  }

  return suggestions.slice(0, 5) // Return max 5 suggestions
}

/**
 * Get agency by slug
 */
export async function getAgencyBySlug(slug: string) {
  try {
    const { data, error } = await supabase
      .from('agencies')
      .select('*')
      .eq('slug', slug)
      .single()

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found
        return null
      }
      // Handle 406 Not Acceptable error
      if (error.message?.includes('406')) {
        console.warn('406 Not Acceptable error, retrying with explicit headers...');
        // Retry with direct fetch as fallback
        return await getAgencyBySlugFallback(slug);
      }
      throw error
    }

    return data
  } catch (error) {
    console.error('Error fetching agency by slug:', error)
    // Try fallback on any error
    try {
      return await getAgencyBySlugFallback(slug);
    } catch (fallbackError) {
      console.error('Fallback also failed:', fallbackError);
      return null;
    }
  }
}

/**
 * Fallback function to get agency by slug using direct fetch
 */
async function getAgencyBySlugFallback(slug: string) {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
  
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase configuration');
  }

  const url = `${supabaseUrl}/rest/v1/agencies?select=*&slug=eq.${encodeURIComponent(slug)}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'apikey': supabaseAnonKey,
      'Authorization': `Bearer ${supabaseAnonKey}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Prefer': 'return=representation'
    }
  });

  if (!response.ok) {
    if (response.status === 406) {
      throw new Error('406 Not Acceptable - API may not support requested format');
    }
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  return data && data.length > 0 ? data[0] : null;
}

/**
 * Update agency slug
 */
export async function updateAgencySlug(agencyId: string, newSlug: string) {
  try {
    // Validate format
    if (!isValidSlugFormat(newSlug)) {
      throw new Error('Invalid slug format. Must be 3-50 characters, lowercase alphanumeric with hyphens.')
    }

    // Check availability
    if (!(await isSlugAvailable(newSlug, agencyId))) {
      throw new Error('This username is already taken. Please choose a different one.')
    }

    // Update in database
    const { data, error } = await supabase
      .from('agencies')
      .update({ slug: newSlug })
      .eq('id', agencyId)
      .select()
      .single()

    if (error) {
      throw error
    }

    return data
  } catch (error) {
    console.error('Error updating agency slug:', error)
    throw error
  }
}

/**
 * Reserved slugs that cannot be used by agencies
 */
export const RESERVED_SLUGS = [
  'admin',
  'api',
  'app',
  'www',
  'mail',
  'ftp',
  'blog',
  'help',
  'support',
  'about',
  'contact',
  'terms',
  'privacy',
  'login',
  'signup',
  'register',
  'dashboard',
  'profile',
  'settings',
  'cars',
  'agencies',
  'brands',
  'cities',
  'locations',
  'search',
  'book',
  'booking',
  'bookings',
  'payment',
  'payments',
  'invoice',
  'invoices',
  'contract',
  'contracts',
  'review',
  'reviews',
  'notification',
  'notifications',
  'kiracar',
  'kira',
  'car',
  'rental',
  'rent',
  'location',
  'voiture'
]

/**
 * Check if slug is reserved
 */
export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.includes(slug.toLowerCase())
}

/**
 * Comprehensive slug validation (includes reserved check)
 */
export function validateSlug(slug: string): { isValid: boolean; error?: string } {
  if (!slug || slug.trim() === '') {
    return { isValid: false, error: 'Username is required' }
  }

  const trimmedSlug = slug.trim().toLowerCase()

  if (!isValidSlugFormat(trimmedSlug)) {
    return { 
      isValid: false, 
      error: 'Username must be 3-50 characters, lowercase letters, numbers, and hyphens only. Cannot start or end with hyphen.' 
    }
  }

  if (isReservedSlug(trimmedSlug)) {
    return { 
      isValid: false, 
      error: 'This username is reserved. Please choose a different one.' 
    }
  }

  return { isValid: true }
}
